import clientAxios from "../client";

import { formatQueryString } from "@/common/utils/common";

import type { QueryType } from "@/common/types/common";
import type { AnalysisType } from "@/common/types/analysis";
import type { BreakPayloadType } from "@/common/types/breaks";

export async function analysisList(
    evaluation_id: string,
    query?: QueryType
) {
    const queryString = formatQueryString(query);

    return await clientAxios.get(
        `account/evaluation/${evaluation_id}/analysis/list${queryString}`
    );
}

export async function analysisAdd(
    evaluation_id: string,
) {
    return await clientAxios.post(
        `account/evaluation/${evaluation_id}/analysis/add`,
        {}
    );
}

export async function analysisGet(
    analysis_id: string
) {
    return await clientAxios.get(
        `account/analysis/${analysis_id}/show`
    );
}

export async function analysisFull(
    analysis_id: string
) {
    return await clientAxios.get(
        `account/analysis/${analysis_id}/show/full`
    );
}

export async function analysisPDF(
    analysis_id: string,
    token: string
) {
    return await clientAxios.get(
        `account/analysis/${analysis_id}/show/pdf?token=${token}`
    );
}

export async function analysisDownloadPDF(
    analysis_id: string,
    payload: string
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/download/pdf`,
        { payload }
    );
}

export async function analysisPatch(
    analysis_id: string,
    payload: Partial<AnalysisType>
) {
    return await clientAxios.patch(
        `account/analysis/${analysis_id}/patch`,
        { payload }
    );
}

export async function analysisStart(
    analysis_id: string
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/start`,
        null
    );
}

export async function analysisCancel(
    analysis_id: string
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/cancel`,
        null
    );
}

export async function analysisPause(
    analysis_id: string,
    payload: BreakPayloadType
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/break`,
        payload
    );
}

export async function analysisPauseUpdate(
    break_id: string,
    payload: BreakPayloadType
) {
    return await clientAxios.put(
        `common/break/${break_id}/edit`,
        payload
    );
}

export async function analysisResume(
    analysis_id: string,
    break_id: string,
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/break/${break_id}/resume`,
        null
    );
}

export async function analysisStop(
    analysis_id: string
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/stop`,
        null
    );
}

export async function analysisPut(
    analysis_id: string,
    payload: AnalysisType
) {
    return await clientAxios.put(
        `account/analysis/${analysis_id}/edit`,
        payload
    );
}

export async function analysisDel(
    analysis_id: string
) {
    return await clientAxios.del(
        `account/analysis/${analysis_id}/delete`
    );
}

export async function analysisCopy(
    analysis_id: string
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/copy`, null
    );
}

// FORM
export async function analysisFormList(
    analysis_id: string,
) {
    return await clientAxios.get(
        `account/analysis/${analysis_id}/form/list`,
    );
}

export async function analysisFormAdd(
    analysis_id: string,
    form_id: string,
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/form/add`,
        { form_id }
    );
}

export async function analysisFormDel(
    analysis_id: string,
    form_id: string,
) {
    return await clientAxios.del(
        `account/analysis/${analysis_id}/form/${form_id}/delete`,
    );
}

// METHODS
export async function analysisMethodList(
    analysis_id: string,
) {
    return await clientAxios.get(
        `account/analysis/${analysis_id}/method/list`,
    );
}

export async function analysisMethodAdd(
    analysis_id: string,
    method_id: string,
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/method/add`,
        { method_id }
    );
}

export async function analysisMethodDel(
    analysis_id: string,
    method_id: string,
) {
    return await clientAxios.del(
        `account/analysis/${analysis_id}/method/${method_id}/delete`,
    );
}

// EQUIPMENT
export async function analysisEquipmentList(
    analysis_id: string,
) {
    return await clientAxios.get(
        `account/analysis/${analysis_id}/equipment/list`,
    );
}

export async function analysisEquipmentAdd(
    analysis_id: string,
    equipment_id: string,
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/equipment/add`,
        { equipment_id }
    );
}

export async function analysisEquipmentDel(
    analysis_id: string,
    equipment_id: string,
) {
    return await clientAxios.del(
        `account/analysis/${analysis_id}/equipment/${equipment_id}/delete`,
    );
}

// SAMPLE
export async function analysisSampleList(
    analysis_id: string,
) {
    return await clientAxios.get(
        `account/analysis/${analysis_id}/sample/list`,
    );
}

export async function analysisSampleAdd(
    analysis_id: string,
    sample_id: string,
) {
    return await clientAxios.post(
        `account/analysis/${analysis_id}/sample/add`,
        { sample_id }
    );
}

export async function analysisSampleDel(
    analysis_id: string,
    sample_id: string,
) {
    return await clientAxios.del(
        `account/analysis/${analysis_id}/sample/${sample_id}/delete`,
    );
}